import { z } from 'zod';

export const tagSchema = z.enum([
  'fragile', 'liquid', 'battery', 'medication', 'document', 'first_access',
  'clothing', 'toiletry', 'electronics', 'health', 'utility', 'food', 'family',
]);

export type Tag = z.infer<typeof tagSchema>;
export type OwnerId = string | 'shared';

export const dimensionsSchema = z.object({
  width: z.number().int().positive().max(2_000),
  height: z.number().int().positive().max(2_000),
  depth: z.number().int().positive().max(2_000),
});

export type DimensionsMm = z.infer<typeof dimensionsSchema>;

export const bagKindSchema = z.enum(['carry_on', 'backpack', 'personal', 'custom']);
export const bagRoleSchema = z.enum(['cabin', 'personal', 'checked']);
export const containerKindSchema = z.enum(['main', 'lid', 'pocket', 'cube']);

export const bagSchema = z.object({
  id: z.string().min(1),
  tripId: z.string().min(1),
  name: z.string().min(1).max(80),
  kind: bagKindSchema,
  role: bagRoleSchema.optional(),
  outer: dimensionsSchema.optional(),
  measuredAt: z.string().datetime().optional(),
  emptyWeightG: z.number().int().nonnegative().max(100_000),
  weightSource: z.enum(['estimated', 'measured']).optional(),
  confirmedAllowanceG: z.number().int().positive().max(100_000).optional(),
  allowanceConfirmedAt: z.string().datetime().optional(),
  ruleCandidateId: z.string().min(1).optional(),
  allowanceGroupId: z.string().min(1).optional(),
  allowanceGroupLimitG: z.number().int().positive().max(100_000).optional(),
  containerIds: z.array(z.string().min(1)).min(1),
  priority: z.number().int().nonnegative(),
});

export type Bag = z.infer<typeof bagSchema>;

export const containerSchema = z.object({
  id: z.string().min(1),
  bagId: z.string().min(1),
  parentContainerId: z.string().min(1).optional(),
  name: z.string().min(1).max(80),
  kind: containerKindSchema,
  inner: dimensionsSchema,
  layerCount: z.number().int().min(1).max(20),
  maxDepthMmPerLayer: z.number().int().positive().max(2_000),
  originX: z.number().int().nonnegative(),
  originY: z.number().int().nonnegative(),
  priority: z.number().int().nonnegative(),
});

export type Container = z.infer<typeof containerSchema>;

export const itemPresetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(2).max(80),
  category: z.string().min(1).max(40),
  packed: dimensionsSchema,
  weightG: z.number().int().positive().max(100_000),
  rotatable: z.boolean(),
  compressible: z.boolean(),
  tags: z.array(tagSchema),
  source: z.enum(['builtin', 'user']),
});

export type ItemPreset = z.infer<typeof itemPresetSchema>;

export const checklistStatusSchema = z.enum(['planned', 'to_buy', 'ready', 'packed']);
export type ChecklistStatus = z.infer<typeof checklistStatusSchema>;

export const accessLevelSchema = z.enum(['quick', 'standard', 'deep']);
export type AccessLevel = z.infer<typeof accessLevelSchema>;

export const itemInstanceSchema = z.object({
  id: z.string().min(1),
  tripId: z.string().min(1),
  presetId: z.string().min(1).optional(),
  name: z.string().min(1).max(80),
  ownerId: z.string().min(1),
  recipientId: z.string().min(1).max(120).optional(),
  packed: dimensionsSchema.optional(),
  packedDimensionsState: z.literal('unknown').optional(),
  weightG: z.number().int().positive().max(100_000).optional(),
  weightSource: z.enum(['unknown', 'estimated', 'measured']).optional(),
  packMoment: z.enum(['anytime', 'departure_morning', 'leaving_home']).optional(),
  rotatable: z.boolean(),
  quantityIndex: z.number().int().positive(),
  tags: z.array(tagSchema),
  note: z.string().max(240).optional(),
  buyUrl: z.string().url().startsWith('https://').max(2_000).optional(),
  priceKrw: z.number().int().nonnegative().max(1_000_000_000).optional(),
  liquidMl: z.number().nonnegative().max(100_000).optional(),
  batteryWh: z.number().positive().max(10_000).optional(),
  used: z.boolean().optional(),
  manuallyUnplaced: z.boolean().optional(),
  placementId: z.string().min(1).optional(),
  assignedBagId: z.string().min(1).optional(),
  checklistStatus: checklistStatusSchema.optional(),
  accessLevel: accessLevelSchema.optional(),
}).refine((item) => item.packedDimensionsState === 'unknown' ? item.packed === undefined : item.packed !== undefined, { message: '치수 미확인 자료에 추정 치수를 넣지 않습니다.' })
  .refine((item) => item.weightSource === 'unknown' ? item.weightG === undefined : item.weightG !== undefined, { message: '미측정 무게에 예시 값을 넣지 않습니다.' });

export type ItemInstance = z.infer<typeof itemInstanceSchema>;

export const placementSchema = z.object({
  id: z.string().min(1),
  itemInstanceId: z.string().min(1),
  containerId: z.string().min(1),
  zLayer: z.number().int().nonnegative(),
  xMm: z.number().int().nonnegative(),
  yMm: z.number().int().nonnegative(),
  orientation: z.union([z.literal(0), z.literal(90)]),
  source: z.enum(['auto', 'manual']),
  createdAt: z.string().datetime(),
});

export type Placement = z.infer<typeof placementSchema>;

export const weatherProfileSchema = z.enum(['hot', 'mild', 'cold', 'rainy', 'mixed']);
export type WeatherProfile = z.infer<typeof weatherProfileSchema>;

export const flightSegmentSchema = z.object({
  id: z.string().min(1),
  origin: z.string().max(80).optional(),
  destination: z.string().max(80).optional(),
  marketingCarrierCode: z.string().max(8).optional(),
  operatingCarrierCode: z.string().max(8).optional(),
  flightNumber: z.string().max(16).optional(),
  fareFamily: z.string().max(80).optional(),
});

export type FlightSegment = z.infer<typeof flightSegmentSchema>;

export const ruleSnapshotSchema = z.object({
  candidateId: z.string().min(1),
  carrierCode: z.string().min(1).max(8),
  carrierName: z.string().min(1).max(80),
  allowanceG: z.number().int().positive().max(100_000),
  mainBag: dimensionsSchema.optional(),
  personalItem: dimensionsSchema.optional(),
  sourceUrl: z.string().url(),
  sourceCheckedAt: z.string().date(),
  confirmedAt: z.string().datetime(),
});

export type RuleSnapshot = z.infer<typeof ruleSnapshotSchema>;

export const journeySchema = z.object({
  destination: z.string().max(80).optional(),
  departureDate: z.string().date().optional(),
  returnDate: z.string().date().optional(),
  weatherProfile: weatherProfileSchema.optional(),
  segments: z.array(flightSegmentSchema).max(8).optional(),
  ruleSnapshot: ruleSnapshotSchema.optional(),
});

export type Journey = z.infer<typeof journeySchema>;

export const departureCheckSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(160),
  phase: z.enum(['week_before', 'day_before', 'leaving', 'airport']).optional(),
  checked: z.boolean(),
  updatedAt: z.string().datetime().optional(),
});

export type DepartureCheck = z.infer<typeof departureCheckSchema>;

export const scheduleEntrySchema = z.object({
  id: z.string().min(1).max(120),
  date: z.string().date().optional(),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
  endDate: z.string().date().optional(),
  endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
  timezone: z.string().min(1).max(80).optional(),
  kind: z.enum(['flight', 'lodging', 'transport', 'activity', 'meal', 'task']),
  title: z.string().min(1).max(120),
  place: z.string().max(120).optional(),
  address: z.string().max(240).optional(),
  googleMapsUrl: z.string().url().max(2_000).optional(),
  mapUrl: z.string().url().startsWith('https://').max(2_000).optional(),
  note: z.string().max(1_000).optional(),
  privateNote: z.string().max(500).optional(),
  participantIds: z.array(z.string().min(1)).max(20).optional(),
  order: z.number().int().nonnegative().max(10_000).optional(),
  positioned: z.boolean().optional(),
  completed: z.boolean(),
});

export type ScheduleEntry = z.infer<typeof scheduleEntrySchema>;

export const travelDocumentSchema = z.object({
  id: z.string().min(1).max(120),
  kind: z.enum(['passport', 'visa', 'insurance', 'reservation', 'esim', 'other']),
  label: z.string().min(1).max(120),
  status: z.enum(['needed', 'checking', 'ready']),
  dueDate: z.string().date().optional(),
  note: z.string().max(240).optional(),
});

export type TravelDocument = z.infer<typeof travelDocumentSchema>;

export const participantRoleSchema = z.enum(['owner', 'editor', 'viewer', 'traveler']);
export type ParticipantRole = z.infer<typeof participantRoleSchema>;

export const tripParticipantSchema = z.object({
  id: z.string().min(1).max(120),
  name: z.string().min(1).max(40),
  role: participantRoleSchema,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  note: z.string().max(160).optional(),
  createdAt: z.string().datetime(),
});

export type TripParticipant = z.infer<typeof tripParticipantSchema>;

export const photoAttachmentSchema = z.object({
  id: z.string().min(1).max(120),
  name: z.string().min(1).max(120),
  mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  size: z.number().int().positive().max(2_500_000),
  width: z.number().int().positive().max(8_000),
  height: z.number().int().positive().max(8_000),
  dataUrl: z.string().startsWith('data:image/').max(3_500_000),
  caption: z.string().max(240).optional(),
  locationIncluded: z.literal(false),
  createdAt: z.string().datetime(),
});

export type PhotoAttachment = z.infer<typeof photoAttachmentSchema>;

export const tripMemorySchema = z.object({
  id: z.string().min(1).max(120),
  date: z.string().date().optional(),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
  title: z.string().min(1).max(120),
  note: z.string().max(2_000).optional(),
  place: z.string().max(120).optional(),
  address: z.string().max(240).optional(),
  googleMapsUrl: z.string().url().max(2_000).optional(),
  mapUrl: z.string().url().startsWith('https://').max(2_000).optional(),
  participantIds: z.array(z.string().min(1)).max(20).optional(),
  linkedScheduleEntryId: z.string().min(1).max(120).optional(),
  photos: z.array(photoAttachmentSchema).max(12),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type TripMemory = z.infer<typeof tripMemorySchema>;

export const sharePermissionSchema = z.enum(['view', 'edit']);
export type SharePermission = z.infer<typeof sharePermissionSchema>;

export const tripShareSettingsSchema = z.object({
  permission: sharePermissionSchema,
  includePacking: z.boolean(),
  packingOwner: z.string().min(1).max(40).optional(),
  includePhotos: z.boolean(),
  includeExpenses: z.boolean().optional(),
  expiresInDays: z.union([z.literal(7), z.literal(30), z.literal(90)]),
});

export type TripShareSettings = z.infer<typeof tripShareSettingsSchema>;

export const remoteShareSchema = z.object({
  shareId: z.string().min(8).max(120),
  editToken: z.string().min(16).max(500),
  encryptionKey: z.string().min(32).max(500),
  revision: z.number().int().positive(),
  permission: sharePermissionSchema,
  expiresAt: z.string().datetime(),
  lastSyncedAt: z.string().datetime().optional(),
});

export type RemoteShare = z.infer<typeof remoteShareSchema>;

export const expenseSchema = z.object({
  id: z.string().min(1).max(120), date: z.string().date(),
  title: z.string().min(1).max(80), amountMinor: z.number().int().nonnegative().max(1_000_000_000_000),
  currency: z.string().regex(/^[A-Z]{3}$/), paidBy: z.string().min(1).max(120),
  splitAmong: z.array(z.string().min(1).max(120)).min(1).max(20),
  category: z.enum(['교통', '숙소', '식사', '관광', '쇼핑', '기타']),
  note: z.string().max(240).optional(),
});
export type Expense = z.infer<typeof expenseSchema>;

export const TRIP_TITLE_MAX_LENGTH = 80;
export const tripSchema = z.object({
  id: z.string().min(1),
  schemaVersion: z.literal(1),
  title: z.string().min(1).max(TRIP_TITLE_MAX_LENGTH),
  status: z.enum(['draft', 'packed', 'active', 'archived']),
  ownerIds: z.array(z.string().min(1)),
  bagIds: z.array(z.string().min(1)),
  itemInstanceIds: z.array(z.string().min(1)),
  extractionOrder: z.array(z.string().min(1)),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  sourceTripId: z.string().min(1).optional(),
  statusBeforeArchive: z.enum(['draft', 'packed', 'active']).optional(),
  lastExportedAt: z.string().datetime().optional(),
  expenses: z.array(expenseSchema).max(500).optional(),
  currencyRates: z.record(z.string().regex(/^[A-Z]{3}$/), z.number().positive().max(1_000_000)).optional(),
  postTripReviewedAt: z.string().datetime().optional(),
  packingReview: z.array(z.object({ key: z.string().min(1), usedQuantity: z.number().int().min(0).max(200), extraQuantity: z.number().int().min(0).max(30) })).max(200).optional(),
  forgottenItems: z.array(z.object({ id: z.string().min(1), name: z.string().min(1).max(80), presetId: z.string().optional(), weightG: z.number().int().positive().max(100_000).optional(), quantity: z.number().int().min(1).max(30) })).max(30).optional(),
  journey: journeySchema.optional(),
  departureChecks: z.array(departureCheckSchema).max(24).optional(),
  scheduleEntries: z.array(scheduleEntrySchema).max(100).optional(),
  travelDocuments: z.array(travelDocumentSchema).max(40).optional(),
  participants: z.array(tripParticipantSchema).max(20).optional(),
  memories: z.array(tripMemorySchema).max(200).optional(),
  shareSettings: tripShareSettingsSchema.optional(),
  remoteShare: remoteShareSchema.optional(),
});

export type Trip = z.infer<typeof tripSchema>;

export const packingStateSchema = z.object({
  schemaVersion: z.literal(1),
  trip: tripSchema,
  bags: z.array(bagSchema),
  containers: z.array(containerSchema),
  items: z.array(itemInstanceSchema),
  placements: z.array(placementSchema),
});

export type PackingState = z.infer<typeof packingStateSchema>;

export const savedBagProfileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(80),
  kind: bagKindSchema,
  role: bagRoleSchema,
  outer: dimensionsSchema.optional(),
  emptyWeightG: z.number().int().nonnegative().max(100_000),
  compartments: z.array(z.object({
    name: z.string().min(1).max(80),
    kind: containerKindSchema,
    inner: dimensionsSchema,
    layerCount: z.number().int().min(1).max(20),
  })).min(1).max(12),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SavedBagProfile = z.infer<typeof savedBagProfileSchema>;

export function validateStateReferences(state: PackingState): string[] {
  const errors: string[] = [];
  const bagIds = new Set(state.bags.map((bag) => bag.id));
  const containerIds = new Set(state.containers.map((container) => container.id));
  const itemIds = new Set(state.items.map((item) => item.id));
  const placementItemIds = new Set<string>();
  const participantIds = new Set((state.trip.participants ?? []).map((entry) => entry.id));
  const scheduleIds = new Set((state.trip.scheduleEntries ?? []).map((entry) => entry.id));
  for (const entries of [state.bags, state.containers, state.items, state.placements, state.trip.participants ?? [], state.trip.scheduleEntries ?? [], state.trip.memories ?? []]) {
    if (new Set(entries.map((entry) => entry.id)).size !== entries.length) errors.push('duplicate entity id');
  }
  for (const entry of [...(state.trip.scheduleEntries ?? []), ...(state.trip.memories ?? [])]) {
    for (const id of entry.participantIds ?? []) if (!participantIds.has(id)) errors.push(`missing participant:${id}`);
  }
  for (const memory of state.trip.memories ?? []) {
    if (memory.linkedScheduleEntryId && !scheduleIds.has(memory.linkedScheduleEntryId)) errors.push(`missing schedule:${memory.linkedScheduleEntryId}`);
  }
  for (const bag of state.bags) {
    if (bag.tripId !== state.trip.id) errors.push('bag belongs to another trip');
    for (const id of bag.containerIds) if (!containerIds.has(id)) errors.push(`missing container:${id}`);
  }
  for (const item of state.items) {
    if (item.tripId !== state.trip.id) errors.push('item belongs to another trip');
    if (item.assignedBagId && !bagIds.has(item.assignedBagId)) errors.push(`missing assigned bag:${item.assignedBagId}`);
  }

  for (const id of state.trip.bagIds) if (!bagIds.has(id)) errors.push(`missing bag:${id}`);
  for (const id of state.trip.itemInstanceIds) if (!itemIds.has(id)) errors.push(`missing item:${id}`);
  for (const container of state.containers) {
    if (!bagIds.has(container.bagId)) errors.push(`container ${container.id} missing bag:${container.bagId}`);
    if (container.parentContainerId && !containerIds.has(container.parentContainerId)) errors.push(`container ${container.id} missing parent:${container.parentContainerId}`);
  }
  for (const placement of state.placements) {
    if (state.items.some((item) => item.id === placement.itemInstanceId && !item.packed)) errors.push('unmeasured item has placement');
    if (!itemIds.has(placement.itemInstanceId)) errors.push(`placement ${placement.id} missing item`);
    if (!containerIds.has(placement.containerId)) errors.push(`placement ${placement.id} missing container`);
    if (placementItemIds.has(placement.itemInstanceId)) errors.push(`item ${placement.itemInstanceId} has multiple placements`);
    placementItemIds.add(placement.itemInstanceId);
  }
  return errors;
}
